import { Injectable } from '@angular/core';
import * as GameSocketActions from './gameSocket.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { Chips, TableType } from '@soposopo/sopo-models';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { GameSocketState, Socket, SocketConnectionStatus } from './gameSocket.state';
import { firstValueFrom, Observable } from 'rxjs';
import { GameServer } from '../../backend/game-server/base/game-server.class';
import { GameStateId } from '../game/game.state';
import { GameDispatcherActions } from '../game/game.actions';

@Injectable({
    providedIn: 'root'
})
export class GameSocketStoreDispatcher {
    private servers: {[key: string]: GameServer} = {};
    constructor(private store: Store<AppState>) {}

    public newGameServer(gameServer: GameServer, id: string): void {
        this.servers[id] = gameServer;
    }

    public isGameServerCreated(id: string): boolean {
        return !!this.getGameServer(id);
    }

    private getGameServer(id: string): GameServer {
        return this.servers[id];
    }

    public deleteGameServer(id: string) {
        delete this.servers[id];
        this.store.dispatch(GameDispatcherActions.removeGameFromGamesList({gameStateId: id}));
    }

    bet(amount: Chips, gameStateId: GameStateId) {
        this.store.dispatch(GameSocketActions.betSignaledAction({bet: amount, gameStateId}));
    }

    call(gameStateId: GameStateId) {
        this.store.dispatch(GameSocketActions.callSignaledAction({gameStateId}));
    }

    callAny(gameStateId: GameStateId) {
        this.store.dispatch(GameSocketActions.callAnySignaledAction({gameStateId}));
    }

    check(gameStateId: GameStateId) {
        this.store.dispatch(GameSocketActions.checkSignaledAction({gameStateId}));
    }

    checkOrFold(gameStateId: GameStateId) {
        this.store.dispatch(GameSocketActions.checkOrFoldSignaledAction({gameStateId}));
    }

    connect(gameStateId: GameStateId, socketId: string) {
        this.store.dispatch(GameSocketActions.gameSocketConnectedAction({gameStateId, socketId}));
    }

    disconnect(gameStateId: GameStateId) {
        this.store.dispatch(GameSocketActions.gameSocketDisconnectedAction({gameStateId}));
    }

    createSocket(gameStateId: GameStateId, url: string, gameType: TableType) {
        this.store.dispatch(GameSocketActions.createGameSocketAction({gameStateId, url, gameType}));
    }

    fold(gameStateId: GameStateId) {
        this.store.dispatch(GameSocketActions.foldSignaledAction({gameStateId}));
    }

    raise(amount: Chips, gameStateId: GameStateId) {
        this.store.dispatch(GameSocketActions.raiseSignaledAction({raise: amount, gameStateId}));
    }

    acceptDealerSignal(message: string) {
        this.store.dispatch(GameSocketActions.dealerSignalAcceptedAction({message}));
    }

    acceptTableSignal(message: string) {
        this.store.dispatch(GameSocketActions.tableSignalAcceptedAction({message}));
    }

    rejectSignal(message: string) {
        this.store.dispatch(GameSocketActions.signalRejectedAction({reason: message}));
    }

    topUp(amount: Chips, isBuyIn: boolean, nextMaxBuyIn: number, gameStateId: GameStateId) {
        this.store.dispatch(GameSocketActions.topUpSignaledAction({amount, isBuyIn, nextMaxBuyIn, gameStateId}));
    }

    getStore(): Store<AppState> {
        return this.store;
    }

    async getGameSocketState(): Promise<GameSocketState> {
        return firstValueFrom(this.store.select('gameSocket'));
    }

    getGameSocketStateObservable(): Observable<GameSocketState> {
        return this.store.select('gameSocket');
    }

    getGameSocket(gameStateId: GameStateId): Observable<GameServer> {
        return this.getGameSocketStateObservable().pipe(
            map(state => state.sockets),
            filter(sockets => sockets.find(socket => socket.gameStateId === gameStateId) !== undefined),
            map(sockets => (sockets.find(socket => socket.gameStateId === gameStateId) as Socket).gameServerId),
            map(id => this.getGameServer(id))
        )
    }

    getGameSocketAsPromise(gameStateId: GameStateId) {
        return firstValueFrom(this.getGameSocket(gameStateId));
    }

    getUnfilteredGameSocketAsPromise(gameStateId: GameStateId): Promise<GameServer | undefined> {
        return firstValueFrom(
            this.getGameSocketStateObservable().pipe(
                map(state => state.sockets),
                map(sockets => {
                    const socket = sockets.find(socket => socket.gameStateId === gameStateId)
                    return socket ? this.getGameServer(socket.gameServerId) : undefined;
                }),
            )
        )
    }

    getGameSocketStatusAndId(gameStateId: GameStateId): Observable<{connectionStatus: SocketConnectionStatus, socketId: string}> {
        return this.getGameSocketStateObservable().pipe(
            map(state => state.sockets),
            filter(sockets => sockets.find(socket => socket.gameStateId === gameStateId) !== undefined),
            map(sockets => {
                const socket = sockets.find(socket => socket.gameStateId === gameStateId);
                return socket ? { connectionStatus: socket.connectionStatus, socketId: socket.socketId } : { connectionStatus: 'disconnected' as SocketConnectionStatus, socketId: '' };
            }),
            distinctUntilChanged((prev, curr) => prev.connectionStatus === curr.connectionStatus && prev.socketId === curr.socketId),
        )
    }

    getGameSocketStatus(gameStateId: GameStateId): Observable<SocketConnectionStatus> {
        return this.getGameSocketStatusAndId(gameStateId).pipe(
            map(statusAndId => statusAndId.connectionStatus),
            distinctUntilChanged(),
        );
    }

    socketIsConnected(gameStateId: GameStateId): Promise<boolean> {
        return firstValueFrom(this.getGameSocketStatus(gameStateId).pipe(
            map(socketConnectionStatus => socketConnectionStatus === 'connected')
        ));
    }

    isTournament(gameStateId: GameStateId) {
        return firstValueFrom(this.getGameSocketStateObservable().pipe(
            map(state => state.sockets),
            filter(sockets => sockets.find(socket => socket.gameStateId === gameStateId) !== undefined),
            map(sockets => (sockets.find(socket => socket.gameStateId === gameStateId) as Socket)),
            map(socket => socket.gameType === TableType.SNG || socket.gameType === TableType.MTT),
        ));
    }

    getTableType(gameStateId: GameStateId) {
        return firstValueFrom(this.getGameSocketStateObservable().pipe(
            map(state => state.sockets),
            filter(sockets => sockets.find(socket => socket.gameStateId === gameStateId) !== undefined),
            map(sockets => (sockets.find(socket => socket.gameStateId === gameStateId) as Socket)),
            map(socket => socket.gameType),
        ));
    }

    getServerUrl(gameStateId: GameStateId) {
        return firstValueFrom(this.getGameSocketStateObservable().pipe(
            map(state => state.sockets),
            map(sockets => {
                const socket = sockets.find(socket => socket.gameStateId === gameStateId);
                return socket ? socket.url : '';
            }),
        ));
    }

    removeSocket(gameStateId: GameStateId) {
        const socket = this.getGameServer(gameStateId);
        if (!socket) return;
        socket.disconnect();
        this.store.dispatch(GameSocketActions.removeGameSocket({gameStateId}));
    }
}
